import React, { Suspense, useCallback, useMemo, useRef } from 'react';
import { ErrorBoundary } from '@funtech-int/rival-react';
import {
    Picture,
    createPictureFactory,
    useAnalytics,
    useAuthManager,
    usePaymentPlace,
} from '@funtech-int/yango-shared';
import classnames from 'classnames';

import { PlusPaymentPlace } from '@/shared/constants/payments';
import { useLocale } from '@/shared/hooks/useLocale';
import { Button } from '@/shared/components/Button/Button';
import { getAutoOpenWidgetUrl } from '@/shared/lib/getAutoOpenWidgetUrl';
import { Spinner, SpinnerSize } from '@/shared/components/Spinner';
import { ArrowBottom } from '@/shared/components/ArrowBottom/ArrowBottom';
import { useSubscriptionRefetch } from '@/shared/hooks/useSubscriptionRefetch';
import { useLoginUrl } from '@/shared/hooks/useLoginUrl';
import { usePaymentPage } from '@/shared/hooks/usePaymentPage';
import { useOfferWebAnalytics } from '@/shared/hooks/useOfferWebAnalytics';
import { usePaymentWidgetModal } from '@/shared/hooks/usePopups';

import { I18n, useI18n } from '../../hooks/useI18n';
import { getChannelsPaywallImageUrl } from '../../lib/utils/getChannelsPaywallImageUrl';
import { createAnalyticsEvents } from '../../lib/createAnalyticsEvents';

import styles from './ChannelsPaywall.module.css';

const useSubscriptionOfferButtonText = (
    i18n: I18n,
    offerText?: string,
    isLoginwallEnabled?: boolean
) => {
    if (isLoginwallEnabled) {
        return i18n('subscription_offer_button_get_started');
    }

    return offerText || i18n('subscription_offer_button_subscribe');
};

const pictureFactory = createPictureFactory({
    fallback: {
        srcSet: {
            '1x': '1440x720',
            '2x': '2016x1134',
        },
        src: '1440x720',
    },
});

interface ChannelsPaywallComponentProps {
    scrollToNextSection: VoidFunction;
}

const ChannelsPaywallComponent = ({ scrollToNextSection }: ChannelsPaywallComponentProps) => {
    const { dir } = useLocale();
    const i18n = useI18n();

    const { isAuthorized } = useAuthManager();

    const { refetch: subscriptionRefetch } = useSubscriptionRefetch();

    const page = usePaymentPage();
    const paymentPlace = usePaymentPlace({
        page,
        places: [PlusPaymentPlace.PaywallBaseOfferButton],
    });

    const offer = paymentPlace.selectOfferBy({
        place: PlusPaymentPlace.PaywallBaseOfferButton,
    });

    const analytics = useAnalytics(createAnalyticsEvents);

    const buttonText = useSubscriptionOfferButtonText(
        i18n,
        offer?.offerTexts.buttonText,
        !isAuthorized
    );

    const offerAnalytics = useMemo(
        () => analytics.createOfferAnalyticsEvents(offer, buttonText, '', ''),
        [analytics, offer, buttonText]
    );

    const imageUrl = getChannelsPaywallImageUrl(
        offer?.offerTexts?.customTexts?.desktopBackgroundUrl,
        dir
    );

    const { img } = pictureFactory({
        fallbackUrl: imageUrl,
    });

    const offerLoginUrl = useLoginUrl(getAutoOpenWidgetUrl(offer));

    const offerWebAnalytics = useOfferWebAnalytics(offer);

    const paymentWidget = usePaymentWidgetModal();

    const controller = useMemo(() => {
        function onPurchaseSuccess() {
            subscriptionRefetch();
            offerWebAnalytics.purchaseSucceeded();

            if (offer) {
                offerAnalytics.succeeded();
            }
        }

        return {
            onOfferShow: async () => {
                if (!offer) return;

                paymentPlace?.analytics.offerShowed(offer);
                offerAnalytics.showed();
                offerWebAnalytics.offerShowed();
            },
            onOfferClick: () => {
                if (!offer) return;

                paymentPlace?.analytics.offerClicked(offer);
                offerAnalytics.navigated();
                offerWebAnalytics.offerClicked();
                paymentWidget.open(paymentPlace, offer, {
                    onPurchaseSuccess,
                    onNeedAuth: () => window.location.assign(offerLoginUrl),
                });
            },
        };
    }, [
        offer,
        offerAnalytics,
        offerLoginUrl,
        offerWebAnalytics,
        paymentWidget,
        paymentPlace,
        subscriptionRefetch,
    ]);

    const additionText = offer?.offerTexts?.buttonTextWithDetails;
    const shouldShowAdditionText = additionText && isAuthorized;

    return (
        <>
            {imageUrl && (
                <Picture
                    className={styles.coverImage}
                    alt=""
                    loading="lazy"
                    srcSet={img.srcSet}
                    src={img.src}
                    data-tid="AdaptiveImage"
                />
            )}
            <div className={styles.offer}>
                <h3 className={styles.title}>
                    {offer?.offerTexts?.customTexts?.paywallTitle ?? i18n('channels_paywall_title')}
                </h3>

                {offer?.target && (
                    <div className={styles.purchaseButton}>
                        <Button
                            size={52}
                            variant={isAuthorized ? 'plus' : 'accent'}
                            onClick={controller.onOfferClick}
                            isFullWidth={false}
                            data-test-id="SubscriptionPurchaseButton"
                            onShow={controller.onOfferShow}
                        >
                            {buttonText}
                        </Button>

                        {shouldShowAdditionText && (
                            <p
                                className={classnames(
                                    styles.additionText,
                                    styles.purchaseButtonSubtext
                                )}
                            >
                                {additionText}
                            </p>
                        )}
                    </div>
                )}
            </div>
            <div className={styles.arrowBottom}>
                <ArrowBottom
                    onScrollTo={scrollToNextSection}
                    title={i18n('channels_arrow_button_title')}
                />
            </div>
        </>
    );
};

export const ChannelsPaywall = () => {
    const mainRef = useRef<HTMLDivElement>(null);

    const scrollToNextSection = useCallback(() => {
        const heightMain =
            (mainRef.current && mainRef.current.offsetHeight + mainRef.current.offsetTop) || 0;

        window.scrollTo({ top: heightMain, behavior: 'smooth' });
    }, [mainRef]);

    return (
        <ErrorBoundary fallback={() => null}>
            <div className={styles.root} ref={mainRef}>
                <Suspense
                    fallback={<Spinner className={styles.spinner} size={SpinnerSize.MEDIUM} />}
                >
                    <ChannelsPaywallComponent scrollToNextSection={scrollToNextSection} />
                </Suspense>
            </div>
        </ErrorBoundary>
    );
};
